import { useEffect, useRef, useState } from "react";
import { getPendingBuildId, onNewBuildAvailable } from "@/lib/buildVersion";

/**
 * Route-change remount strategy for the editor preview iframe.
 *
 * When a new build is published while the iframe is open, the running provider
 * tree keeps stale context state (auth/role caches, route flags, onboarding,
 * theme settings) even after modules hot-update. Instead of a disruptive
 * mid-page reload, we wait for the next route change and remount the whole
 * provider subtree by bumping a key — so context state resets at a natural
 * navigation boundary.
 *
 * Active only inside an iframe or local dev; on standalone published tabs the
 * existing build guard (reload prompt) handles updates.
 */
function inPreviewFrame() {
  if (typeof window === "undefined") return false;
  if (import.meta.env.DEV) return true;
  try {
    return window.self !== window.top;
  } catch {
    return true;
  }
}

/** Fires the callback on every SPA navigation, including pushState/replaceState. */
function subscribeToNavigation(onNavigate: (path: string) => void): () => void {
  const history = window.history;
  const originalPush = history.pushState;
  const originalReplace = history.replaceState;

  const emit = () => onNavigate(window.location.pathname + window.location.search);

  history.pushState = function patchedPushState(...args) {
    const result = originalPush.apply(this, args as Parameters<typeof originalPush>);
    emit();
    return result;
  };
  history.replaceState = function patchedReplaceState(...args) {
    const result = originalReplace.apply(this, args as Parameters<typeof originalReplace>);
    emit();
    return result;
  };
  window.addEventListener("popstate", emit);

  return () => {
    history.pushState = originalPush;
    history.replaceState = originalReplace;
    window.removeEventListener("popstate", emit);
  };
}

export function usePreviewRemountKey(): number {
  const [remountKey, setRemountKey] = useState(0);
  const pendingRef = useRef<string | null>(null);
  const pathRef = useRef(
    typeof window === "undefined" ? "" : window.location.pathname + window.location.search,
  );

  useEffect(() => {
    if (!inPreviewFrame()) return;

    pendingRef.current = getPendingBuildId();
    const unsubscribeBuild = onNewBuildAvailable((buildId) => {
      pendingRef.current = buildId;
    });

    // In dev, a Vite HMR update is the equivalent signal to "a new build shipped".
    let unsubscribeHmr: (() => void) | undefined;
    if (import.meta.hot) {
      const markDirty = () => {
        pendingRef.current = `hmr-${Date.now()}`;
      };
      import.meta.hot.on("vite:afterUpdate", markDirty);
      unsubscribeHmr = () => import.meta.hot?.off("vite:afterUpdate", markDirty);
    }

    const unsubscribeNav = subscribeToNavigation((path) => {
      if (path === pathRef.current) return;
      pathRef.current = path;
      if (!pendingRef.current) return;
      // Reset the flag first so a single update remounts exactly once.
      pendingRef.current = null;
      setRemountKey((key) => key + 1);
    });

    return () => {
      unsubscribeBuild();
      unsubscribeHmr?.();
      unsubscribeNav();
    };
  }, []);

  return remountKey;
}
